import type { History, Location } from "history";

import { getObjectValueFromPath } from "utilities/object/object";

/**
 * Parent key under `location.state` that namespaces all action markers, so we
 * don't collide with other consumers of history state.
 */
export const ACTION_MARKERS_KEY = "actionMarkers";

/** Action keys live here so consumers don't pass raw strings. */
export const MANAGE_SAVED_VIEWS = "manageSavedViews";
export const MANAGE_SAVED_FILTERS = "manageSavedFilters";

export type HistoryActionKey = typeof MANAGE_SAVED_VIEWS | typeof MANAGE_SAVED_FILTERS;

type ActionMarkers = {
    [key in HistoryActionKey]?: boolean;
};

function readMarkers(state: unknown): ActionMarkers {
    if (state && typeof state === "object") {
        const markers = getObjectValueFromPath<ActionMarkers>(state, ACTION_MARKERS_KEY);
        if (markers && typeof markers === "object") {
            return markers;
        }
    }
    return {};
}

/**
 * Check whether the given action is marked in the current location's history state.
 */
export function isActionMarkedInHistory(location: Location, actionKey: HistoryActionKey): boolean {
    return readMarkers(location.state)[actionKey] === true;
}

/**
 * Check the current browser history state directly. Use this where a `Location`
 * is not available — e.g. class component field initializers that need to derive
 * initial state on mount.
 */
export function isActionMarkedInWindowHistory(actionKey: HistoryActionKey): boolean {
    if (typeof window === "undefined") {
        return false;
    }
    // react-router v5 (history v4) stores the user state at window.history.state.state
    const userState = (window.history.state as { state?: unknown } | null)?.state;
    return readMarkers(userState)[actionKey] === true;
}

/**
 * Push a new history entry that marks the action as open. Call this when the
 * consumer transitions from "no action" to "action open". The mobile back
 * button will pop this entry and clear the marker.
 */
export function pushActionToHistory(
    location: Location,
    history: History,
    actionKey: HistoryActionKey,
) {
    const existing = readMarkers(location.state);
    history.push({
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
        state: {
            ...((location.state as object | null) ?? {}),
            [ACTION_MARKERS_KEY]: {
                ...existing,
                [actionKey]: true,
            },
        },
    });
}

/**
 * Pop the most recent history entry, which removes the action marker. Call
 * this when the consumer programmatically closes the action.
 */
export function popActionFromHistory(history: History) {
    history.goBack();
}
